/*
 * Copyright (c) 2024 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import { createAction } from '../../../../utils/redux-utils';
import {
    CashBoxTasksListRequestParams,
    CreateCashBoxTaskRequestParams,
    CashBoxTasksListDto,
    SuitableTargetVersionsParams,
} from '../../../api/service/serviceClient.typings';
import { NOTIFICATION_TYPES, Notification } from '../../../../utils/utils';
import { i18n } from '../../../translation/i18n';

export enum CashBoxTasksTabActionType {
    OPEN_CASHBOX_TASKS_TAB = 'CashBoxTasksTab/ open tab',
    EXIT_CASHBOX_TASKS_TAB = 'CashBoxTasksTab/ exit tab',

    REQUEST_CERT_UPDATE_START = 'CashBoxTasksTab/ request token certificate update start',
    REQUEST_CERT_UPDATE_SUCCESS = 'CashBoxTasksTab/ request token certificate update success',
    REQUEST_CERT_UPDATE_ERROR = 'CashBoxTasksTab/ request token certificate update error',
    FETCH_CERT_UPDATES_HISTORY_START = 'CashBoxTasksTab/ fetch token certificate updates history start',
    FETCH_CERT_UPDATES_HISTORY_SUCCESS = 'CashBoxTasksTab/ fetch token certificate updates history success',
    FETCH_CERT_UPDATES_HISTORY_ERROR = 'CashBoxTasksTab/ fetch token certificate updates history error',

    REQUEST_LOGS_START = 'CashBoxTasksTab/ request cashbox logs start',
    REQUEST_LOGS_SUCCESS = 'CashBoxTasksTab/ request cashbox logs success',
    REQUEST_LOGS_ERROR = 'CashBoxTasksTab/ request cashbox logs error',
    FETCH_LOGS_HISTORY_START = 'CashBoxTasksTab/ fetch cashbox logs requests history start',
    FETCH_LOGS_HISTORY_SUCCESS = 'CashBoxTasksTab/ fetch cashbox logs requests history success',
    FETCH_LOGS_HISTORY_ERROR = 'CashBoxTasksTab/ fetch cashbox logs requests history error',
    DOWNLOAD_CASHBOX_LOGS_START = 'CashBoxTasksTab/ download cashbox logs start',
    DOWNLOAD_CASHBOX_LOGS_SUCCESS = 'CashBoxTasksTab/ download cashbox logs success',
    DOWNLOAD_CASHBOX_LOGS_ERROR = 'CashBoxTasksTab/ download cashbox logs error',

    REQUEST_FIRMWARE_UPDATE_START = 'CashBoxTasksTab/ request firmware update start',
    REQUEST_FIRMWARE_UPDATE_SUCCESS = 'CashBoxTasksTab/ request firmaware update success',
    REQUEST_FIRMWARE_UPDATE_ERROR = 'CashBoxTasksTab/ request firmware update error',
    FETCH_FIRMWARE_UPDATES_HISTORY_START = 'CashBoxTasksTab/ fetch firmware updates history start',
    FETCH_FIRMWARE_UPDATES_HISTORY_SUCCESS = 'CashBoxTasksTab/ fetch firmware updates history success',
    FETCH_FIRMWARE_UPDATES_HISTORY_ERROR = 'CashBoxTasksTab/ fetch firmware updates history error',
    FETCH_SUITABLE_TARGET_VERSIONS_START = 'CashBoxTasksTab/ fetch suitable target versions start',
    FETCH_SUITABLE_TARGET_VERSIONS_SUCCESS = 'CashBoxTasksTab/ fetch suitable target versions success',
    FETCH_SUITABLE_TARGET_VERSIONS_ERROR = 'CashBoxTasksTab/ fetch suitable target versions error',

    REQUEST_ENV_INFO_START = 'CashBoxTasksTab/ request environment info start',
    REQUEST_ENV_INFO_SUCCESS = 'CashBoxTasksTab/ request environment info success',
    REQUEST_ENV_INFO_ERROR = 'CashBoxTasksTab/ request environment info error',
    FETCH_ENV_INFO_HISTORY_START = 'CashBoxTasksTab/ fetch environment info history start',
    FETCH_ENV_INFO_HISTORY_SUCCESS = 'CashBoxTasksTab/ fetch environment info history success',
    FETCH_ENV_INFO_HISTORY_ERROR = 'CashBoxTasksTab/ fetch environment info history error',
}

export const openCashBoxTasksTabAction = () => createAction(CashBoxTasksTabActionType.OPEN_CASHBOX_TASKS_TAB);

export const exitCashBoxTasksTabAction = () => createAction(CashBoxTasksTabActionType.EXIT_CASHBOX_TASKS_TAB);

// ------------------------ certificate update

export const requestCertUpdateStartAction = (params: CreateCashBoxTaskRequestParams) =>
    createAction(CashBoxTasksTabActionType.REQUEST_CERT_UPDATE_START, params);

export const requestCertUpdateSuccessAction = () =>
    createAction(CashBoxTasksTabActionType.REQUEST_CERT_UPDATE_SUCCESS, {
        notification: {
            type: NOTIFICATION_TYPES[1],
            message: i18n.t('RegistryScreens.certUpdateRequestCreated'),
        },
    });

export const requestCertUpdateErrorAction = (notification: Notification) =>
    createAction(CashBoxTasksTabActionType.REQUEST_CERT_UPDATE_ERROR, { notification });

export const fetchCertUpdatesHistoryStartAction = (params: CashBoxTasksListRequestParams) =>
    createAction(CashBoxTasksTabActionType.FETCH_CERT_UPDATES_HISTORY_START, params);

export const fetchCertUpdatesHistorySuccessAction = (payload: CashBoxTasksListDto) =>
    createAction(CashBoxTasksTabActionType.FETCH_CERT_UPDATES_HISTORY_SUCCESS, payload);

export const fetchCertUpdatesHistoryErrorAction = (notification: Notification) =>
    createAction(CashBoxTasksTabActionType.FETCH_CERT_UPDATES_HISTORY_ERROR, { notification });

// ------------------------ cashbox logs

export const requestCashBoxLogsStartAction = (params: CreateCashBoxTaskRequestParams) =>
    createAction(CashBoxTasksTabActionType.REQUEST_LOGS_START, params);

export const requestCashBoxLogsSuccessAction = () =>
    createAction(CashBoxTasksTabActionType.REQUEST_LOGS_SUCCESS, {
        notification: {
            type: NOTIFICATION_TYPES[1],
            message: i18n.t('RegistryScreens.logsRequestCreated'),
        },
    });

export const requestCashBoxLogsErrorAction = (notification: Notification) =>
    createAction(CashBoxTasksTabActionType.REQUEST_LOGS_ERROR, { notification });

export const fetchCashBoxLogsHistoryStartAction = (params: CashBoxTasksListRequestParams) =>
    createAction(CashBoxTasksTabActionType.FETCH_LOGS_HISTORY_START, params);

export const fetchCashBoxLogsHistorySuccessAction = (payload: CashBoxTasksListDto) =>
    createAction(CashBoxTasksTabActionType.FETCH_LOGS_HISTORY_SUCCESS, payload);

export const fetchCashBoxLogsHistoryErrorAction = (notification: Notification) =>
    createAction(CashBoxTasksTabActionType.FETCH_LOGS_HISTORY_ERROR, { notification });

export const downloadCashBoxLogsStartAction = (taskId: string, fileName: string) =>
    createAction(CashBoxTasksTabActionType.DOWNLOAD_CASHBOX_LOGS_START, { logId: taskId, fileName });

export const downloadCashBoxLogsSuccessAction = () =>
    createAction(CashBoxTasksTabActionType.DOWNLOAD_CASHBOX_LOGS_SUCCESS);

export const downloadCashBoxLogsErrorAction = (notification: Notification) =>
    createAction(CashBoxTasksTabActionType.DOWNLOAD_CASHBOX_LOGS_ERROR, { notification });

// ------------------------ firmware update

export const requestFirmwareUpdateStartAction = (params: CreateCashBoxTaskRequestParams) =>
    createAction(CashBoxTasksTabActionType.REQUEST_FIRMWARE_UPDATE_START, params);

export const requestFirmwareUpdateSuccessAction = () =>
    createAction(CashBoxTasksTabActionType.REQUEST_FIRMWARE_UPDATE_SUCCESS, {
        notification: {
            type: NOTIFICATION_TYPES[1],
            message: i18n.t('RegistryScreens.firmwareUpdateRequestCreated'),
        },
    });

export const requestFirmwareUpdateErrorAction = (notification: Notification) =>
    createAction(CashBoxTasksTabActionType.REQUEST_FIRMWARE_UPDATE_ERROR, { notification });

export const fetchFirmwareUpdatesHistoryStartAction = (params: CashBoxTasksListRequestParams) =>
    createAction(CashBoxTasksTabActionType.FETCH_FIRMWARE_UPDATES_HISTORY_START, params);

export const fetchFirmwareUpdatesHistorySuccessAction = (payload: CashBoxTasksListDto) =>
    createAction(CashBoxTasksTabActionType.FETCH_FIRMWARE_UPDATES_HISTORY_SUCCESS, payload);

export const fetchFirmwareUpdatesHistoryErrorAction = (notification: Notification) =>
    createAction(CashBoxTasksTabActionType.FETCH_FIRMWARE_UPDATES_HISTORY_ERROR, { notification });

export const fetchSuitableTargetVersionsStartAction = (params: SuitableTargetVersionsParams) =>
    createAction(CashBoxTasksTabActionType.FETCH_SUITABLE_TARGET_VERSIONS_START, params);

export const fetchSuitableTargetVersionsSuccessAction = (payload: string[]) =>
    createAction(CashBoxTasksTabActionType.FETCH_SUITABLE_TARGET_VERSIONS_SUCCESS, payload);

export const fetchSuitableTargetVersionsErrorAction = (notification: Notification) =>
    createAction(CashBoxTasksTabActionType.FETCH_SUITABLE_TARGET_VERSIONS_ERROR, { notification });

// ------------------------ environment info

export const requestEnvInfoStartAction = (params: CreateCashBoxTaskRequestParams) =>
    createAction(CashBoxTasksTabActionType.REQUEST_ENV_INFO_START, params);

export const requestEnvInfoSuccessAction = () =>
    createAction(CashBoxTasksTabActionType.REQUEST_ENV_INFO_SUCCESS, {
        notification: {
            type: NOTIFICATION_TYPES[1],
            message: i18n.t('RegistryScreens.envInfoRequestCreated'),
        },
    });

export const requestEnvInfoErrorAction = (notification: Notification) =>
    createAction(CashBoxTasksTabActionType.REQUEST_ENV_INFO_ERROR, { notification });

export const fetchEnvInfoHistoryStartAction = (params: CashBoxTasksListRequestParams) =>
    createAction(CashBoxTasksTabActionType.FETCH_ENV_INFO_HISTORY_START, params);

export const fetchEnvInfoHistorySuccessAction = (payload: CashBoxTasksListDto) =>
    createAction(CashBoxTasksTabActionType.FETCH_ENV_INFO_HISTORY_SUCCESS, payload);

export const fetchEnvInfoHistoryErrorAction = (notification: Notification) =>
    createAction(CashBoxTasksTabActionType.FETCH_ENV_INFO_HISTORY_ERROR, { notification });

// ------------------------

export type CashBoxTasksTabAction =
    | ReturnType<typeof openCashBoxTasksTabAction>
    | ReturnType<typeof exitCashBoxTasksTabAction>
    | ReturnType<typeof requestCertUpdateStartAction>
    | ReturnType<typeof requestCertUpdateSuccessAction>
    | ReturnType<typeof requestCertUpdateErrorAction>
    | ReturnType<typeof fetchCertUpdatesHistoryStartAction>
    | ReturnType<typeof fetchCertUpdatesHistorySuccessAction>
    | ReturnType<typeof fetchCertUpdatesHistoryErrorAction>
    | ReturnType<typeof requestCashBoxLogsStartAction>
    | ReturnType<typeof requestCashBoxLogsSuccessAction>
    | ReturnType<typeof requestCashBoxLogsErrorAction>
    | ReturnType<typeof fetchCashBoxLogsHistoryStartAction>
    | ReturnType<typeof fetchCashBoxLogsHistorySuccessAction>
    | ReturnType<typeof fetchCashBoxLogsHistoryErrorAction>
    | ReturnType<typeof downloadCashBoxLogsStartAction>
    | ReturnType<typeof downloadCashBoxLogsSuccessAction>
    | ReturnType<typeof downloadCashBoxLogsErrorAction>
    | ReturnType<typeof requestFirmwareUpdateStartAction>
    | ReturnType<typeof requestFirmwareUpdateSuccessAction>
    | ReturnType<typeof requestFirmwareUpdateErrorAction>
    | ReturnType<typeof fetchFirmwareUpdatesHistoryStartAction>
    | ReturnType<typeof fetchFirmwareUpdatesHistorySuccessAction>
    | ReturnType<typeof fetchFirmwareUpdatesHistoryErrorAction>
    | ReturnType<typeof fetchSuitableTargetVersionsStartAction>
    | ReturnType<typeof fetchSuitableTargetVersionsSuccessAction>
    | ReturnType<typeof fetchSuitableTargetVersionsErrorAction>
    | ReturnType<typeof requestEnvInfoStartAction>
    | ReturnType<typeof requestEnvInfoSuccessAction>
    | ReturnType<typeof requestEnvInfoErrorAction>
    | ReturnType<typeof fetchEnvInfoHistoryStartAction>
    | ReturnType<typeof fetchEnvInfoHistorySuccessAction>
    | ReturnType<typeof fetchEnvInfoHistoryErrorAction>;
